import React, { useEffect, useState } from 'react';
import {
  Card,
  Title3,
  Body1,
  Spinner,
  makeStyles,
  tokens,
  Caption1,
  Badge,
} from '@fluentui/react-components';
import {
  CheckmarkCircle24Regular,
  DismissCircle24Regular,
  Warning24Regular,
  Document24Regular,
} from '@fluentui/react-icons';
import { useApi } from '../../contexts/ApiContext';
import type { LogsMetrics } from '../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '16px',
  },
  metricCard: {
    padding: '20px',
  },
  metricHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  metricIcon: {
    fontSize: '24px',
  },
  metricValue: {
    fontSize: '32px',
    fontWeight: '600',
    lineHeight: '1',
  },
  metricLabel: {
    color: tokens.colorNeutralForeground2,
    marginTop: '4px',
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
  },
});

export const LogsDashboard: React.FC = () => {
  const styles = useStyles();
  const apiClient = useApi();
  const [metrics, setMetrics] = useState<LogsMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<LogsMetrics>('/logs/metrics', {
        params: { hoursAgo: 24 }
      });
      setMetrics(response.data);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar métricas');
      console.error('Error loading metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spinner label="Carregando métricas..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <Body1 className={styles.errorText}>Erro: {error}</Body1>
      </Card>
    );
  }

  if (!metrics) {
    return <Body1>Nenhuma métrica disponível</Body1>;
  }

  return (
    <div className={styles.container}>
      <Title3>Métricas de Logs (Últimas 24h)</Title3>

      <div className={styles.cardsGrid}>
        {/* Total Messages */}
        <Card className={styles.metricCard}>
          <div className={styles.metricHeader}>
            <Document24Regular className={styles.metricIcon} />
            <div>
              <div className={styles.metricValue}>{metrics.totalMessages}</div>
              <Caption1 className={styles.metricLabel}>Total de Mensagens</Caption1>
            </div>
          </div>
        </Card>

        {/* Success Messages */}
        <Card className={styles.metricCard} style={{ borderLeft: `4px solid ${tokens.colorPaletteGreenBorder1}` }}>
          <div className={styles.metricHeader}>
            <CheckmarkCircle24Regular className={styles.metricIcon} style={{ color: tokens.colorPaletteGreenForeground1 }} />
            <div>
              <div className={styles.metricValue}>{metrics.successMessages}</div>
              <Caption1 className={styles.metricLabel}>Sucesso</Caption1>
            </div>
          </div>
        </Card>

        {/* Error Messages */}
        <Card className={styles.metricCard} style={{ borderLeft: `4px solid ${tokens.colorPaletteRedBorder1}` }}>
          <div className={styles.metricHeader}>
            <DismissCircle24Regular className={styles.metricIcon} style={{ color: tokens.colorPaletteRedForeground1 }} />
            <div>
              <div className={styles.metricValue}>{metrics.errorMessages}</div>
              <Caption1 className={styles.metricLabel}>Erros</Caption1>
            </div>
          </div>
        </Card>

        {/* Warning Messages */}
        <Card className={styles.metricCard} style={{ borderLeft: `4px solid ${tokens.colorPaletteYellowBorder1}` }}>
          <div className={styles.metricHeader}>
            <Warning24Regular className={styles.metricIcon} style={{ color: tokens.colorPaletteYellowForeground1 }} />
            <div>
              <div className={styles.metricValue}>{metrics.warningMessages}</div>
              <Caption1 className={styles.metricLabel}>Avisos</Caption1>
            </div>
          </div>
        </Card>
      </div>

      {/* Message Type Breakdown */}
      {Object.keys(metrics.messageTypeBreakdown).length > 0 && (
        <Card style={{ padding: '20px' }}>
          <Title3 style={{ marginBottom: '16px' }}>Por Tipo de Mensagem</Title3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {Object.entries(metrics.messageTypeBreakdown).map(([type, count]) => (
              <Badge key={type} appearance="outline" size="large">
                {type}: {count}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {/* Operation Breakdown */}
      {Object.keys(metrics.operationBreakdown).length > 0 && (
        <Card style={{ padding: '20px' }}>
          <Title3 style={{ marginBottom: '16px' }}>Por Operação</Title3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {Object.entries(metrics.operationBreakdown).map(([operation, count]) => (
              <Badge key={operation} appearance="tint" size="large">
                {operation}: {count}
              </Badge>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default LogsDashboard;
